import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col } from "reactstrap";
import Coverage from "./Coverage";
import AnalyticsCard from "./AnalyticsCard";
import PartiallyIncompletedAttributes from "./PartiallyIncompletedAttributes";
import {
  target_group,
  product_category,
  style,
  price,
  brand_rating_options,
  getCamelCaseWord
} from "../../static/brands_wholesalers_data";
import SizeImg from '../../static/take-look-black.png';
import { getCoverageData } from "../../ReduxToolkit/Reducers/analytics";
import { setSelectedFilters } from "../../ReduxToolkit/Reducers/retailers";
import { useNavigate } from "react-router-dom";

const subTabOptions = [
  { label: "New Algo", value: "new_algo" },
  { label: "Old Algo", value: "old_algo" },
];

// Attributes that are the ONLY missing one on a partially incompleted profile.
// Card titles double as the only_missing_attribute filter value lookup below.
const ONLY_MISSING_ICONS = {
  target_group: () => target_group[0]?.imageBlack,
  product_category: () => product_category[0]?.imageBlack,
  style: () => style[0]?.imageBlack,
  price: () => price[0]?.imageBlack,
}

const onlyMissingCardTitle = (label) => `${label} Is Only Missing Attribute In`

// Card title -> the label the list filter expects, e.g. "Target Group".
export const onlyMissingLabelFromTitle = (coverage, title) =>
  (coverage?.only_missing_attributes || []).find(
    (item) => onlyMissingCardTitle(item.label) === title,
  )?.label

const onlyMissingCards = (coverage) =>
  (coverage?.only_missing_attributes || []).map((item) => ({
    title: onlyMissingCardTitle(item.label),
    count: item.count || 0,
    icon: ONLY_MISSING_ICONS[item.attribute]?.(),
    isImageIcon: true,
  }))

const RetailersAnalytics = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [subTab, setSubTab] = useState("new_algo");
  const [location, setLocation] = useState("");
  const [createdDateRange, setCreatedDateRange] = useState({ startDate: null, endDate: null });
  const [updatedDateRange, setUpdatedDateRange] = useState({ startDate: null, endDate: null });
  const { retailersCoverage, isLoading } = useSelector((state) => state.analytics);

  useEffect(() => {
    dispatch(getCoverageData('Retailers', null, subTab));
  }, [subTab, dispatch]);

  const handleLocationSelect = (selectedLocation) => {
    setLocation(selectedLocation)
    dispatch(getCoverageData(
      'Retailers',
      selectedLocation || null,
      subTab,
      createdDateRange?.startDate ? createdDateRange : null,
      updatedDateRange?.startDate ? updatedDateRange : null
    ));
  };

  const handleDateRangeChange = (newCreatedDateRange, newUpdatedDateRange) => {
    dispatch(getCoverageData(
      'Retailers',
      location || null,
      subTab,
      newCreatedDateRange?.startDate ? newCreatedDateRange : null,
      newUpdatedDateRange?.startDate ? newUpdatedDateRange : null
    ));
  };

  // Must mirror the analytics query's own scope exactly (fashion / valid website /
  // retailer entity / algorithm subtab / the dashboard's date range), or the table
  // count will not match the card that linked to it.
  const getBaseFilters = () => ({
    entity: ['Retailer', 'Shop'],
    website_status: ['Valid'],
    summary_type: ['Fashion'],
    algorithm: [subTab === 'old_algo' ? 'Old Algorithm' : 'New Algorithm'],
    ...(createdDateRange?.startDate && { createdStartDate: createdDateRange.startDate }),
    ...(createdDateRange?.endDate && { createdEndDate: createdDateRange.endDate }),
    ...(updatedDateRange?.startDate && { updatedStartDate: updatedDateRange.startDate }),
    ...(updatedDateRange?.endDate && { updatedEndDate: updatedDateRange.endDate }),
  })

  // Jump to the retailers list scoped to the partially incompleted profiles that
  // are missing this specific attribute.
  const handleAttributeView = (attribute) => {
    const filterKeyByAttribute = {
      target_group: 'target group',
      product_category: 'categories',
      style: 'style',
      price: 'price',
    }
    const filterKey = filterKeyByAttribute[attribute]
    if (!filterKey) return

    const filters = { ...getBaseFilters(), profile_status: ['Partially Incomplete'], [filterKey]: ['Null Records'] }
    if (location) filters.location = [getCamelCaseWord(location)]

    dispatch(setSelectedFilters({ filters }))
    navigate('/retailers')
  }

  const handleView = (cardName) => {
    const filters = getBaseFilters()
    if (cardName === "Completed Retailers") {
      filters.profile_status = ['Completed']
    } else if (cardName === "Partially Incompleted Retailers") {
      filters.profile_status = ['Partially Incomplete']
    } else if (cardName === "Total Empty Profiles") {
      filters.profile_status = ['Empty']
    } else if (cardName === "Retailers With Matches") {
      filters.match_status = ["With Matches"]
    } else if (cardName === "Completed With Working With") {
      filters.profile_status = ['Completed']
      filters.working_with_status = ['Have Working With']
    } else if (cardName === "Completed Without Working With") {
      filters.profile_status = ['Completed']
      filters.working_with_status = ['Not Have Working With']
    } else if (cardName === "Partially Incompleted With Working With") {
      filters.profile_status = ['Partially Incomplete']
      filters.working_with_status = ['Have Working With']
    } else if (cardName === "Partially Incompleted Without Working With") {
      filters.profile_status = ['Partially Incomplete']
      filters.working_with_status = ['Not Have Working With']
    } else {
      // "<Attribute> Is Only Missing Attribute In" cards
      const onlyMissingLabel = onlyMissingLabelFromTitle(retailersCoverage, cardName)
      if (onlyMissingLabel) filters.only_missing_attribute = [onlyMissingLabel]
    }
    if (location) filters.location = [getCamelCaseWord(location)]

    if (Object.keys(filters).length > 0) dispatch(setSelectedFilters({ filters }))
    console.log(filters)
    navigate('/retailers')
  }

  const coverageData = retailersCoverage ? {
    map: retailersCoverage.map || {},
    cards: [
      { title: "Total Retailers (completed + partially incompleted)", count: retailersCoverage?.total_completed + retailersCoverage?.total_incompleted || 0 },
      { title: "Completed Retailers", count: retailersCoverage.total_completed || 0 },
      { title: "Partially Incompleted Retailers", count: retailersCoverage.total_incompleted || 0 },
      { title: "Retailers With Matches", count: retailersCoverage.with_matches || 0 },
      // { title: "Retailers With Similar Retailer", count: retailersCoverage.with_similar_retailer === "Coming soon" ? "Coming soon" : retailersCoverage.with_similar_retailer || 0 },
      { title: "Total Empty Profiles", count: retailersCoverage?.total_empty || 0 }
    ],
  } : null;


  const additionalCards = [
    {
      title: "Completed With Working With",
      count: retailersCoverage?.completed_with_working_with || 0,
      isImageIcon: false
    },
    {
      title: "Completed Without Working With",
      count: retailersCoverage?.completed_without_working_with || 0,
      isImageIcon: false
    },
    {
      title: "Partially Incompleted With Working With",
      count: retailersCoverage?.incompleted_with_working_with || 0,
      isImageIcon: false
    },
    {
      title: "Partially Incompleted Without Working With",
      count: retailersCoverage?.incompleted_without_working_with || 0,
      isImageIcon: false
    },
    ...onlyMissingCards(retailersCoverage),
  ];

  return (
    <div>
      <div style={{ display: "flex", gap: "0", marginTop: "20px", borderBottom: "2px solid #e5e7eb" }}>
        {subTabOptions.map((t) => (
          <div
            key={t.value}
            onClick={() => setSubTab(t.value)}
            style={{
              padding: "12px 24px",
              cursor: "pointer",
              borderBottom: subTab === t.value ? "3px solid #0d6efd" : "3px solid transparent",
              marginBottom: "-2px",
              transition: "all 0.3s ease",
            }}
          >
            <p
              style={{
                fontWeight: subTab === t.value ? 'bold' : '500',
                fontSize: "16px",
                margin: 0,
                color: subTab === t.value ? "#0d6efd" : "#6c757d",
              }}>
              {t.label}
            </p>
          </div>
        ))}
      </div>

      {subTab === 'old_algo' && coverageData && (
        <>
          <Coverage
            data={coverageData}
            loading={isLoading}
            onlyShow="retailer"
            onLocationSelect={handleLocationSelect}
            onCardViewClick={handleView}
            location={location}
            setLocation={setLocation}
            createdDateRange={createdDateRange}
            setCreatedDateRange={setCreatedDateRange}
            updatedDateRange={updatedDateRange}
            setUpdatedDateRange={setUpdatedDateRange}
            onDateRangeChange={handleDateRangeChange}
          />

          <PartiallyIncompletedAttributes
            attributes={retailersCoverage?.incompleted_attributes}
            total={retailersCoverage?.partially_incompleted_total}
            isLoading={isLoading}
            onViewClick={handleAttributeView}
          />

          <Row className="g-3 mt-3">
            {additionalCards.map((card, index) => (
              <Col sm={6} md={4} key={index}>
                <AnalyticsCard
                  title={card.title}
                  count={card.count}
                  icon={card.icon}
                  isLoading={isLoading}
                  isImageIcon={card.isImageIcon}
                  showViewButton={card.showViewButton !== false}
                  onViewClick={handleView}
                />
              </Col>
            ))}
          </Row>
        </>
      )}

      {subTab === 'new_algo' && coverageData && (
        <>
          <Coverage
            data={coverageData}
            loading={isLoading}
            onlyShow="retailer"
            onLocationSelect={handleLocationSelect}
            location={location}
            setLocation={setLocation}
            onCardViewClick={handleView}
            createdDateRange={createdDateRange}
            setCreatedDateRange={setCreatedDateRange}
            updatedDateRange={updatedDateRange}
            setUpdatedDateRange={setUpdatedDateRange}
            onDateRangeChange={handleDateRangeChange}
          />

          <PartiallyIncompletedAttributes
            attributes={retailersCoverage?.incompleted_attributes}
            total={retailersCoverage?.partially_incompleted_total}
            isLoading={isLoading}
            onViewClick={handleAttributeView}
          />

          <div style={{ border: '0.5px solid #d1d1d1', margin: '30px 0px' }} />

          <Row className="g-3 mt-3">
            {additionalCards.map((card, index) => (
              <Col sm={6} md={4} key={index}>
                <AnalyticsCard
                  title={card.title}
                  count={card.count}
                  icon={card.icon}
                  isLoading={isLoading}
                  isImageIcon={card.isImageIcon}
                  showViewButton={card.showViewButton !== false}
                  onViewClick={handleView}
                />
              </Col>
            ))}
          </Row>
        </>
      )}
    </div>
  );
};

export default RetailersAnalytics;